import { useCallback, useEffect, useState } from 'react';
import { Card, Chip, EmptyState } from '@/shared/ui/atoms';
import { apiTokenApi } from '@/features/settings/api';
import type { ApiToken, ApiTokenScope, IssuedToken } from '@/features/settings/types';

/**
 * API 토큰 관리 — 슬랙 봇 같은 콘솔 밖 시스템이 쓰는 토큰을 발급·폐기한다.
 *
 * 평문은 발급 직후 한 번만 보인다. 서버가 해시만 저장하므로 화면이 다시 물어볼 데가 없다 —
 * 그래서 발급 결과를 목록 위에 남겨 두고 사용자가 직접 닫을 때까지 지우지 않는다.
 */
export function ApiTokenView(): React.JSX.Element {
  const [tokens, setTokens] = useState<ApiToken[] | null>(null);
  const [scopes, setScopes] = useState<ApiTokenScope[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [issued, setIssued] = useState<IssuedToken | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<{ id: number; scopes: string[] } | null>(null);

  const load = useCallback(() => {
    apiTokenApi
      .list()
      .then((payload) => {
        setTokens(payload.tokens);
        setError(null);
      })
      .catch((cause: Error) => setError(cause.message));
  }, []);

  useEffect(load, [load]);

  useEffect(() => {
    apiTokenApi
      .scopes()
      .then((payload) => setScopes(payload.scopes))
      .catch((cause: Error) => setError(cause.message));
  }, []);

  const toggleScope = useCallback((key: string) => {
    setPicked((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));
  }, []);

  const issue = useCallback(() => {
    setBusy(true);
    apiTokenApi
      .issue(label.trim(), picked)
      .then((result) => {
        setIssued(result);
        setLabel('');
        setPicked([]);
        load();
      })
      .catch((cause: Error) => setError(cause.message))
      .finally(() => setBusy(false));
  }, [label, picked, load]);

  const saveScopes = useCallback(() => {
    if (!editing) return;
    apiTokenApi
      .changeScopes(editing.id, editing.scopes)
      .then(() => {
        setEditing(null);
        load();
      })
      .catch((cause: Error) => setError(cause.message));
  }, [editing, load]);

  const revoke = useCallback(
    (token: ApiToken) => {
      // 폐기는 되돌릴 수 없고, 그 토큰을 쓰던 시스템이 즉시 401을 받는다.
      if (!window.confirm(`토큰 "${token.label}"을 폐기한다. 이 토큰을 쓰는 시스템은 즉시 호출이 막힌다.`)) return;
      apiTokenApi
        .revoke(token.id)
        .then(load)
        .catch((cause: Error) => setError(cause.message));
    },
    [load],
  );

  return (
    <div className="flex flex-col gap-3">
      <Card title="토큰 발급" hint="발급된 평문은 이 화면에 한 번만 표시된다 — 서버는 해시만 저장한다">
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="어느 시스템에 줄 토큰인가 (예: nd-ai-platform 슬랙 봇)"
          className="w-full rounded-md border border-line px-2.5 py-1.5 text-[13px]"
        />

        <div className="mt-3">
          <div className="mb-1.5 text-[11px] font-semibold tracking-wide text-muted uppercase">권한</div>
          {scopes.length === 0 ? (
            <div className="text-[11.5px] text-faint">발급 가능한 권한을 불러오는 중…</div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {scopes.map((scope) => (
                <label key={scope.key} className="flex cursor-pointer items-start gap-2">
                  <input
                    type="checkbox"
                    checked={picked.includes(scope.key)}
                    onChange={() => toggleScope(scope.key)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="text-[12.5px] font-semibold">{scope.label}</span>
                    <code className="ml-1.5 font-mono text-[11px] text-muted">{scope.key}</code>
                    <span className="block text-[11px] text-faint">{scope.description}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={issue}
            disabled={busy || label.trim() === '' || picked.length === 0}
            className="cursor-pointer rounded-md bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            발급
          </button>
          {/* 권한 없는 토큰은 서버가 거절한다. 왜 버튼이 안 눌리는지 화면에서 먼저 알려준다. */}
          {picked.length === 0 && <span className="text-[11px] text-faint">권한을 하나 이상 골라야 발급된다</span>}
        </div>

        {issued && <IssuedBanner issued={issued} onClose={() => setIssued(null)} />}
      </Card>

      {error && <EmptyState message={`실패 — ${error}`} />}

      <Card title="발급된 토큰" hint="폐기해도 행은 남는다 — 누가 언제 쓰던 토큰인지 추적하기 위해">
        {!tokens ? (
          <EmptyState message="토큰을 불러오는 중…" />
        ) : tokens.length === 0 ? (
          <EmptyState message="발급된 토큰이 없다." />
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="[&>th]:border-b [&>th]:border-line [&>th]:px-2.5 [&>th]:py-2 [&>th]:text-left [&>th]:text-xs [&>th]:font-semibold [&>th]:text-muted">
                <th>이름</th>
                <th className="w-[150px]">접두어</th>
                <th>권한</th>
                <th className="w-[140px]">발급</th>
                <th className="w-[140px]">마지막 사용</th>
                <th className="w-[100px]">상태</th>
                <th className="w-[110px]" />
              </tr>
            </thead>
            <tbody>
              {tokens.map((token) => (
                <tr
                  key={token.id}
                  className="border-b border-line-soft last:border-0 [&>td]:px-2.5 [&>td]:py-2 [&>td]:align-middle [&>td]:text-[12.5px]"
                >
                  <td className={token.revokedAt ? 'text-faint line-through' : ''}>{token.label}</td>
                  <td className="font-mono text-[11.5px] text-muted">{token.prefix}…</td>
                  <td>
                    {editing?.id === token.id ? (
                      <div className="flex flex-col gap-1.5">
                        {scopes.map((scope) => (
                          <label key={scope.key} className="flex cursor-pointer items-center gap-1.5 text-[12px]">
                            <input
                              type="checkbox"
                              checked={editing.scopes.includes(scope.key)}
                              onChange={() =>
                                setEditing({
                                  id: editing.id,
                                  scopes: editing.scopes.includes(scope.key)
                                    ? editing.scopes.filter((key) => key !== scope.key)
                                    : [...editing.scopes, scope.key],
                                })
                              }
                            />
                            {scope.label}
                            <code className="font-mono text-[11px] text-muted">{scope.key}</code>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <ScopeChips scopes={token.scopes} catalog={scopes} />
                    )}
                  </td>
                  <td className="text-[11.5px] text-muted">{localTime(token.createdAt)}</td>
                  <td className="text-[11.5px] text-muted">
                    {token.lastUsedAt ? localTime(token.lastUsedAt) : <span className="text-faint">한 번도 안 씀</span>}
                  </td>
                  <td>{token.revokedAt ? <Chip tone="warn">폐기됨</Chip> : <Chip>사용 중</Chip>}</td>
                  <td>
                    {!token.revokedAt && editing?.id === token.id && (
                      <span className="flex flex-col items-start gap-1">
                        <button
                          type="button"
                          onClick={saveScopes}
                          disabled={editing.scopes.length === 0}
                          className="cursor-pointer text-[11.5px] text-accent hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          저장
                        </button>
                        <button type="button" onClick={() => setEditing(null)} className="cursor-pointer text-[11.5px] text-muted hover:underline">
                          취소
                        </button>
                      </span>
                    )}
                    {!token.revokedAt && editing?.id !== token.id && (
                      <button
                        type="button"
                        onClick={() => setEditing({ id: token.id, scopes: token.scopes })}
                        className="mr-2 cursor-pointer text-[11.5px] text-accent hover:underline"
                      >
                        권한
                      </button>
                    )}
                    {!token.revokedAt && editing?.id !== token.id && (
                      <button
                        type="button"
                        onClick={() => revoke(token)}
                        className="cursor-pointer text-[11.5px] text-rose-700 hover:underline"
                      >
                        폐기
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

/**
 * 권한 칩. 카탈로그에 있으면 사람이 읽는 이름으로, 없으면 원문 문자열로 보여준다 —
 * 없어진 권한을 든 옛 토큰이 화면에서 사라져 보이지 않게.
 */
function ScopeChips({ scopes, catalog }: { scopes: string[]; catalog: ApiTokenScope[] }): React.JSX.Element {
  if (scopes.length === 0) return <span className="text-[11px] text-faint">없음</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {scopes.map((key) => {
        const known = catalog.find((scope) => scope.key === key);
        return (
          <Chip key={key} tone={known ? 'repo' : 'warn'}>
            <span title={known?.description ?? key}>{known?.label ?? key}</span>
          </Chip>
        );
      })}
    </span>
  );
}

function IssuedBanner({ issued, onClose }: { issued: IssuedToken; onClose: () => void }): React.JSX.Element {
  return (
    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
      <div className="text-[12px] font-semibold text-amber-900">
        "{issued.token.label}" 토큰이 발급됐다 — 지금 복사해 두지 않으면 다시 볼 수 없다.
      </div>
      <div className="mt-2 flex items-center gap-2">
        <code className="flex-1 rounded border border-amber-200 bg-surface px-2 py-1.5 font-mono text-[12px] break-all">
          {issued.plaintext}
        </code>
        <button
          type="button"
          onClick={() => void navigator.clipboard.writeText(issued.plaintext)}
          className="cursor-pointer rounded-md border border-line bg-surface px-2.5 py-1.5 text-[11.5px]"
        >
          복사
        </button>
        <button type="button" onClick={onClose} className="cursor-pointer text-[11.5px] text-muted hover:underline">
          닫기
        </button>
      </div>
    </div>
  );
}

function localTime(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });
}
